import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { isChunkError, reloadForNewBuild } from "../lib/lazyWithRetry";

/**
 * Catches render/runtime errors in its subtree so one broken component fails in isolation
 * with a clean fallback instead of blanking the whole page.
 *   variant="page"   → full branded card (routed pages)
 *   variant="inline" → compact notice (embedded widgets like the sticker picker)
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ""}]`, error, info);
    // Item 22 — report the crash to the Alpha Console (best-effort; never throw from here).
    try {
      const body = JSON.stringify({
        error_type: (error && error.name) || "ClientError",
        message: [this.props.label ? `[${this.props.label}] ` : "", (error && error.message) || String(error)].join(""),
        path: typeof window !== "undefined" ? window.location.pathname : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        traceback: (info && info.componentStack) || (error && error.stack) || "",
      });
      const url = `${window.location.origin}/api/client-errors`;
      // Prefer sendBeacon (survives an unmount/navigation); fall back to fetch.
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      } else {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, credentials: "include", keepalive: true }).catch(() => {});
      }
    } catch (e) { /* swallow — reporting must never break the fallback UI */ }
    if (isChunkError(error)) reloadForNewBuild();
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.variant === "inline") {
      return (
        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-900/60 ring-1 ring-slate-800 rounded-lg px-3 py-2" data-testid="error-boundary-inline">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <span>{this.props.label || "This part"} hit a snag.</span>
          <button onClick={this.reset} className="ml-auto text-sky-300 hover:text-sky-200 font-medium" data-testid="error-boundary-retry">Retry</button>
        </div>
      );
    }

    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6" data-testid="error-boundary-page">
        <div className="max-w-md w-full text-center glass rounded-2xl border border-slate-800/60 p-8">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-slate-100 text-lg font-semibold mb-1">This corner of the den stumbled</h2>
          <p className="text-slate-400 text-sm mb-6">
            {this.props.label ? `The ${this.props.label} ran into an unexpected error.` : "Something unexpected happened here."} The rest of the sanctuary is fine — you can try again or head home.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={this.reset} data-testid="error-boundary-retry"
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium rounded-xl px-4 py-2 transition-colors">
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
            <a href="/" className="text-slate-300 hover:text-white text-sm font-medium rounded-xl px-4 py-2 bg-slate-800/60 hover:bg-slate-700/60 transition-colors">
              Back to feed
            </a>
          </div>
        </div>
      </div>
    );
  }
}
